import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, Grow } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import {
  ThemeScope, Button, Input, Label,
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Alert, Badge, Separator,
} from "./ui/shadcn";

// Design tokens (Design 12 - clean SaaS, light, indigo accent)
const INDIGO = "#4f46e5";
const INK = "#0f1222";
const MUTED = "#5b6272";
const EASE = [0.22, 1, 0.36, 1];

const THEME_VARS = {
  "--background": "#ffffff",
  "--foreground": INK,
  "--card": "#ffffff",
  "--card-foreground": INK,
  "--primary": INDIGO,
  "--primary-foreground": "#ffffff",
  "--secondary": "#f3f4f6",
  "--secondary-foreground": "#1f2433",
  "--muted": "#f5f6f9",
  "--muted-foreground": MUTED,
  "--accent": "#f3f4f6",
  "--destructive": "#b91c1c",
  "--destructive-foreground": "#ffffff",
  "--border": "#e3e5ea",
  "--input": "#9097a8",
  "--ring": "#6366f1",
  "--radius": "0.625rem",
};

const MotionButton = motion.create(Button);
const MotionCard = motion.create(Card);

const Kbd = ({ children }) => <kbd className="ld12-kbd">{children}</kbd>;
const EnterKey = () => (
  <Kbd><KeyboardReturnIcon sx={{ fontSize: 13 }} aria-hidden="true" />Enter</Kbd>
);


function LoginDesign12({ connectionString }) {
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();
  const reduce = useReducedMotion();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: INDIGO }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: INDIGO }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: INDIGO }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: INDIGO }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ── Visual helpers (styling / motion only) ─────────────────────────────────
  // Each element carries its OWN initial/animate (no variant chain).
  const enter = (delay = 0, dy = 12) => (reduce ? {} : {
    initial: { opacity: 1, y: dy },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay, ease: EASE },
  });
  // Inputs: transform only, never opacity
  const slide = (delay = 0) => (reduce ? {} : {
    initial: { y: 10 },
    animate: { y: 0 },
    transition: { duration: 0.5, delay, ease: EASE },
  });
  const fadeDur = reduce ? 0.05 : 0.2;

  const handleCompanyKeyDown = (e, idx) => {
    const key = e.key;
    if (key === " ") {
      e.preventDefault();
      setSelectedCompany(companies[idx]);
      return;
    }
    if (key === "Enter") {
      e.preventDefault();
      setSelectedCompany(companies[idx]);
      return;
    }
    let next = null;
    if (key === "ArrowDown" || key === "ArrowRight") next = (idx + 1) % companies.length;
    if (key === "ArrowUp" || key === "ArrowLeft") next = (idx - 1 + companies.length) % companies.length;
    if (next === null) return;
    e.preventDefault();
    setSelectedCompany(companies[next]);
    const el = document.getElementById("ld12-company-" + next);
    if (el) el.focus();
  };


  const capsHelper = (
    <div className="ld12-help">
      <AnimatePresence initial={false}>
        {capsLockOn && (
          <motion.span
            key="caps"
            role="status"
            className="ld12-caps"
            initial={{ opacity: 1, y: reduce ? 0 : -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0.1 : 0.18 }}
          >
            <WarningAmberIcon sx={{ fontSize: 14, color: "#b45309" }} />Caps Lock is ON
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );

  const errorAlert = (
    <AnimatePresence initial={false}>
      {errorMessage && (
        <motion.div
          key="err"
          initial={{ opacity: 1, y: reduce ? 0 : -6 }}
          animate={{ opacity: 1, y: 0, x: reduce ? 0 : [0, -7, 7, -4, 4, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.15 : 0.45 }}
        >
          <Alert variant="destructive" className="ld12-alert">
            <WarningAmberIcon sx={{ fontSize: 18, flexShrink: 0 }} />
            <span>{errorMessage}</span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const css = [
    "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
    ".login12-root, .login12-root * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }",
    ".login12-root .ld12-kbd { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace !important; }",
    ".login12-root { color-scheme: light; }",
    ".ld12-page { position: relative; width: 100%; min-height: 100vh; background: #fff; }",
    "@supports (min-height: 100dvh) { .ld12-page { min-height: 100dvh; } }",

    /* decorative background: static, behind everything */
    ".ld12-bg { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; }",
    ".ld12-grid { position: absolute; inset: 0; background-image: linear-gradient(to right, rgba(15,18,34,.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,18,34,.06) 1px, transparent 1px); background-size: 48px 48px; -webkit-mask-image: radial-gradient(ellipse 85% 75% at 40% 35%, #000 25%, transparent 100%); mask-image: radial-gradient(ellipse 85% 75% at 40% 35%, #000 25%, transparent 100%); }",
    ".ld12-glow { position: absolute; inset: 0; background: radial-gradient(ellipse 50% 40% at 90% 0%, rgba(99,102,241,.08) 0%, transparent 70%); }",

    /* layout: single column (form first) by default, two columns on desktop */
    ".ld12-shell { position: relative; z-index: 1; box-sizing: border-box; width: 100%; max-width: 520px; min-height: inherit; margin: 0 auto; display: grid; grid-template-columns: minmax(0, 1fr); grid-template-areas: 'logo' 'card' 'headline' 'foot'; align-content: start; padding: clamp(16px, 4vw, 40px) clamp(16px, 4vw, 32px) 24px; }",
    ".ld12-logo { grid-area: logo; margin-bottom: clamp(14px, 3vw, 24px); min-width: 0; }",
    ".ld12-logo img { display: block; height: clamp(34px, 5vw, 44px); max-width: 100%; }",
    ".ld12-cardcol { grid-area: card; min-width: 0; position: relative; z-index: 1; width: 100%; }",
    ".ld12-hero { grid-area: headline; min-width: 0; margin-top: clamp(28px, 6vw, 44px); }",
    ".ld12-stepsarea { grid-area: steps; min-width: 0; margin-top: clamp(20px, 4vw, 28px); max-width: 480px; }",
    ".ld12-foot { grid-area: foot; min-width: 0; margin: 28px 0 0; font-size: .75rem; color: " + MUTED + "; word-break: break-word; }",
    "@media (min-width: 1024px) { .ld12-shell { max-width: 1280px; grid-template-columns: minmax(0, 1fr) 440px; grid-template-areas: 'logo card' '. card' 'headline card' '. card' 'foot card'; grid-template-rows: auto 1fr auto 1fr auto; column-gap: clamp(40px, 6vw, 88px); padding: 40px clamp(32px, 4vw, 64px); } .ld12-cardcol { align-self: center; } .ld12-hero { margin-top: 0; } .ld12-foot { margin-top: 0; align-self: end; } .ld12-logo { margin-bottom: 0; } }",
    "@media (max-height: 420px) and (orientation: landscape) { .ld12-hero { display: none; } .ld12-foot { margin-top: 16px; } }",

    /* hero */
    ".ld12-eyebrow { background: #fff; border-color: #d5d9e1; color: #3f4556; gap: .5rem; padding: .3rem .75rem; font-size: .7rem; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; max-width: 100%; }",
    ".ld12-dot { width: 7px; height: 7px; border-radius: 50%; background: #16a34a; flex-shrink: 0; }",
    ".ld12-headline { margin: 1rem 0 .85rem; font-size: clamp(1.6rem, 4.6vw, 3rem); font-weight: 700; line-height: 1.12; letter-spacing: -.03em; color: " + INK + "; max-width: 560px; word-break: break-word; }",
    ".ld12-accent { color: " + INDIGO + "; }",
    ".ld12-sub { margin: 0; font-size: clamp(.9rem, 1.6vw, 1.02rem); line-height: 1.65; color: " + MUTED + "; max-width: 480px; }",
    "@media (max-width: 599.95px) { .ld12-sub { display: none; } }",

    ".ld12-chips { list-style: none; display: flex; flex-wrap: wrap; gap: .5rem; margin: 1.1rem 0 0; padding: 0; }",
    ".ld12-chips li { padding: .3rem .7rem; border: 1px solid #d5d9e1; border-radius: 999px; background: #fff; font-size: .75rem; font-weight: 600; color: #3f4556; }",
    /* checklist */
    ".ld12-steps { overflow: visible; border-color: #dcdfe6; }",
    ".ld12-stepshead { display: flex; align-items: center; justify-content: space-between; gap: .75rem; padding: .8rem 1.1rem; border-bottom: 1px solid var(--border); font-size: .85rem; font-weight: 600; }",
    ".ld12-steplist { list-style: none; margin: 0; padding: .3rem 1.1rem; }",
    ".ld12-step { position: relative; display: flex; gap: .85rem; padding: .7rem 0; min-width: 0; }",
    ".ld12-stepnum { position: relative; z-index: 1; flex-shrink: 0; width: 24px; height: 24px; margin-top: 1px; border-radius: 50%; border: 1.5px solid #9aa1b2; background: #fff; display: flex; align-items: center; justify-content: center; font-size: .72rem; font-weight: 700; color: " + MUTED + "; }",
    ".ld12-step[data-state='current'] .ld12-stepnum { border-color: " + INDIGO + "; color: " + INDIGO + "; }",
    ".ld12-stepring { position: absolute; inset: -4px; border-radius: 50%; border: 2px solid rgba(79,70,229,.3); pointer-events: none; }",
    ".ld12-stepdone { position: absolute; inset: -1.5px; border-radius: 50%; background: " + INDIGO + "; color: #fff; display: flex; align-items: center; justify-content: center; }",
    ".ld12-seg { position: absolute; z-index: 0; left: 11px; top: calc(.7rem + 28px); bottom: calc(4px - .7rem); width: 2px; border-radius: 1px; background: #e3e5ea; overflow: hidden; pointer-events: none; }",
    ".ld12-segfill { display: block; width: 100%; height: 100%; background: " + INDIGO + "; }",
    ".ld12-steptext { display: block; flex: 1; min-width: 0; }",
    ".ld12-steptitle { display: block; font-size: .9rem; font-weight: 600; color: " + INK + "; }",
    ".ld12-stepdesc { display: block; margin-top: 2px; font-size: .8rem; color: " + MUTED + "; overflow-wrap: anywhere; }",
    ".ld12-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }",

    /* card */
    ".login12-root .ld12-card { position: relative; z-index: 1; width: 100%; box-sizing: border-box; border: 1px solid #d5d9e1; border-radius: 14px; background: #fff; box-shadow: 0 1px 2px rgba(15,18,34,.05), 0 18px 40px -24px rgba(15,18,34,.22); }",
    ".login12-root .ld12-head { flex-direction: row; align-items: center; gap: .9rem; padding: clamp(1.1rem, 4vw, 1.6rem) clamp(1.1rem, 4vw, 1.6rem) 1.1rem; }",
    ".login12-root .ld12-tile { width: 42px; height: 42px; flex-shrink: 0; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: " + INDIGO + "; background: #eef0ff; border: 1px solid #dfe2ff; }",
    ".login12-root .ld12-headtext { min-width: 0; display: flex; flex-direction: column; gap: .3rem; }",
    ".login12-root .ld12-title { font-size: clamp(1.2rem, 4.2vw, 1.45rem); font-weight: 700; letter-spacing: -.02em; line-height: 1.15; color: var(--foreground); overflow-wrap: anywhere; }",
    ".login12-root .ld12-desc { font-size: .85rem; line-height: 1.45; color: var(--muted-foreground); }",
    ".login12-root .ld12-card .ui-card-content { padding: 0 clamp(1.1rem, 4vw, 1.6rem) 1.25rem; }",
    ".login12-root .ld12-card .ui-card-footer { flex-direction: column; align-items: flex-start; gap: .8rem; padding: 1rem clamp(1.1rem, 4vw, 1.6rem) 1.25rem; font-size: .875rem; color: var(--muted-foreground); }",

    /* fields */
    ".login12-root .ld12-group { margin-bottom: .2rem; min-width: 0; }",
    ".login12-root .ld12-label { color: var(--foreground); font-weight: 600; font-size: .82rem; line-height: 1.2; margin-bottom: .45rem; }",
    ".login12-root .ld12-labelrow { display: flex; align-items: center; justify-content: space-between; gap: .75rem; margin-bottom: .45rem; flex-wrap: wrap; }",
    ".login12-root .ld12-labelrow .ld12-label { margin-bottom: 0; }",
    ".login12-root .ld12-field { position: relative; min-width: 0; }",
    ".login12-root .ld12-field .ui-input { height: 2.9rem; min-width: 0; border-radius: 10px; border: 1px solid #9097a8; background: #f5f6f9; color: " + INK + "; -webkit-text-fill-color: " + INK + "; caret-color: " + INDIGO + "; opacity: 1; padding: 0 3rem 0 2.6rem; font-size: 1rem; font-weight: 500; }",
    ".login12-root .ld12-field .ld12-pw { padding-right: 5.4rem; }",
    ".login12-root .ld12-field .ui-input::placeholder { color: #6b7280; -webkit-text-fill-color: #6b7280; opacity: 1; font-weight: 400; }",
    ".login12-root .ld12-field .ui-input:hover { border-color: #6b7388; }",
    ".login12-root .ld12-field .ui-input:focus, .login12-root .ld12-field .ui-input:focus-visible { background: #fff; border-color: " + INDIGO + "; box-shadow: 0 0 0 3px rgba(79,70,229,.2); }",
    ".login12-root .ld12-field .ui-input:-webkit-autofill, .login12-root .ld12-field .ui-input:-webkit-autofill:hover { -webkit-box-shadow: 0 0 0 1000px #f5f6f9 inset !important; -webkit-text-fill-color: " + INK + " !important; caret-color: " + INK + " !important; border-color: #9097a8; }",
    ".login12-root .ld12-field .ui-input:-webkit-autofill:focus { -webkit-box-shadow: 0 0 0 1000px #fff inset, 0 0 0 3px rgba(79,70,229,.2) !important; -webkit-text-fill-color: " + INK + " !important; border-color: " + INDIGO + "; }",
    ".login12-root .ld12-field .ui-input:autofill { box-shadow: 0 0 0 1000px #f5f6f9 inset; -webkit-text-fill-color: " + INK + "; color: " + INK + "; }",
    ".login12-root .ld12-fieldicon { position: absolute; z-index: 1; left: .85rem; top: 50%; margin-top: -11px; color: #6b7280; pointer-events: none; }",
    ".login12-root .ld12-field:focus-within .ld12-fieldicon { color: " + INDIGO + "; }",
    ".login12-root .ld12-capsicon { position: absolute; z-index: 1; right: .85rem; top: 50%; margin-top: -10px; pointer-events: none; }",
    ".login12-root .ld12-capsicon-pw { right: 3.4rem; }",
    ".login12-root .ld12-toggle { position: absolute; z-index: 2; right: 0; top: 50%; margin-top: -22px; height: 44px; width: 44px; color: #4b5163; border-radius: 10px; }",
    ".login12-root .ld12-toggle:hover { color: " + INDIGO + "; background: rgba(79,70,229,.08); }",
    ".login12-root .ld12-help { min-height: 1.15rem; padding-top: .3rem; }",
    ".login12-root .ld12-caps { display: inline-flex; align-items: center; gap: .3rem; color: #92400e; font-size: 12px; }",
    ".login12-root .ld12-link { display: inline-flex; align-items: center; min-height: 44px; color: #4338ca; text-decoration: none; font-weight: 600; font-size: .83rem; border-radius: 4px; }",
    ".login12-root .ld12-labelrow .ld12-link { min-height: 28px; }",
    ".login12-root .ld12-link:hover { text-decoration: underline; text-underline-offset: 3px; }",
    ".login12-root .ld12-link:focus-visible { outline: 2px solid " + INDIGO + "; outline-offset: 2px; }",
    ".login12-root .ld12-alert { display: flex; align-items: flex-start; gap: .55rem; background: #fef2f2; border-color: #f4a3a3; color: #991b1b; font-weight: 500; font-size: .83rem; margin-bottom: 1rem; border-radius: 10px; overflow-wrap: anywhere; }",

    /* buttons */
    ".login12-root .ld12-primary { width: 100%; min-height: 44px; height: 2.9rem; border-radius: 10px; font-size: .95rem; font-weight: 600; color: #fff; background: " + INDIGO + "; box-shadow: inset 0 1px 0 rgba(255,255,255,.18), 0 1px 2px rgba(15,18,34,.25), 0 8px 16px -8px rgba(79,70,229,.6); gap: .7rem; }",
    ".login12-root .ld12-primary:hover { opacity: 1; background: #4338ca; }",
    ".login12-root .ld12-primary:disabled { opacity: .75; box-shadow: none; }",
    ".login12-root .ld12-bio { width: 100%; min-height: 44px; margin-top: .75rem; border-radius: 10px; font-weight: 600; color: #4338ca; border-color: #c7caf5; }",
    ".login12-root .ld12-bio:hover { background: #eef0ff; border-color: " + INDIGO + "; }",
    ".login12-root .ld12-kbd { display: inline-flex; align-items: center; gap: .2rem; height: 1.35rem; padding: 0 .4rem; border-radius: 5px; border: 1px solid #d3d7df; border-bottom-width: 2px; background: #fff; color: #3f4556; font-size: .68rem; font-weight: 600; line-height: 1; }",
    ".login12-root .ld12-primary .ld12-kbd { background: rgba(255,255,255,.16); border-color: rgba(255,255,255,.4); color: #fff; }",
    ".login12-root .ld12-hints { display: flex; flex-wrap: wrap; gap: .5rem 1rem; font-size: .75rem; color: " + MUTED + "; }",
    ".login12-root .ld12-hint { display: inline-flex; align-items: center; gap: .4rem; }",
    ".login12-root .ld12-card .ui-sep { margin: 0; }",

    /* company cards */
    ".login12-root .ld12-companylist { display: flex; flex-direction: column; gap: .55rem; max-height: min(320px, 46vh); min-height: 0; overflow-y: auto; padding: 4px; margin: 0 -4px 1rem; }",
    ".login12-root .ld12-company { position: relative; display: flex; align-items: center; gap: .8rem; min-width: 0; min-height: 52px; padding: .65rem .85rem; border-radius: 10px; border: 1px solid #c3c8d4; background: #fff; box-shadow: none; cursor: pointer; outline: none; }",
    ".login12-root .ld12-company:hover { border-color: #8e96a8; }",
    ".login12-root .ld12-company:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--ring); }",
    ".login12-root .ld12-selring { position: absolute; z-index: 0; inset: -1px; border-radius: 10px; border: 2px solid " + INDIGO + "; background: rgba(79,70,229,.05); pointer-events: none; }",
    ".login12-root .ld12-avatar { position: relative; z-index: 1; width: 34px; height: 34px; border-radius: 8px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: .9rem; color: #1f2433; background: #eceef3; }",
    ".login12-root .ld12-company[aria-checked='true'] .ld12-avatar { color: #fff; background: " + INDIGO + "; }",
    ".login12-root .ld12-companyname { position: relative; z-index: 1; flex: 1; min-width: 0; font-weight: 600; font-size: .92rem; color: var(--foreground); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }",
    ".login12-root .ld12-radio { position: relative; z-index: 1; width: 20px; height: 20px; flex-shrink: 0; border-radius: 50%; border: 1.5px solid #9aa1b2; background: #fff; }",
    ".login12-root .ld12-radiofill { position: absolute; inset: -1.5px; border-radius: 50%; background: " + INDIGO + "; color: #fff; display: flex; align-items: center; justify-content: center; }",

    /* session dialog */
    ".login12-root .ld12-dhead { display: flex; align-items: center; gap: .9rem; padding: 1.2rem 1.4rem; border-bottom: 1px solid var(--border); background: #fafafb; }",
    ".login12-root .ld12-dtitle { margin: 0; font-size: 1.05rem; font-weight: 700; color: " + INK + "; line-height: 1.3; }",
    ".login12-root .ld12-dsub { margin: .15rem 0 0; font-size: .8rem; color: " + MUTED + "; }",
    ".login12-root .ld12-dnote { margin: 1rem 1.4rem 0; padding: .65rem .85rem; border-radius: 10px; border: 1px solid var(--border); background: #f5f6f9; font-size: .82rem; line-height: 1.5; color: #3f4556; }",
    ".login12-root .ld12-dlist { padding: 1rem 1.4rem .5rem; }",
    ".login12-root .ld12-dlabel { margin: 0 0 .7rem; font-size: .72rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: " + MUTED + "; }",
    ".login12-root .ld12-session { display: flex; align-items: center; gap: .8rem; min-width: 0; padding: .7rem .8rem; margin-bottom: .55rem; border-radius: 10px; border: 1px solid #c9ced9; background: #fff; }",
    ".login12-root .ld12-sicon { width: 38px; height: 38px; flex-shrink: 0; border-radius: 9px; display: flex; align-items: center; justify-content: center; background: #eef0ff; }",
    ".login12-root .ld12-sinfo { flex: 1; min-width: 0; }",
    ".login12-root .ld12-sname { margin: 0; font-weight: 600; font-size: .87rem; color: " + INK + "; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }",
    ".login12-root .ld12-smeta { display: flex; align-items: center; flex-wrap: wrap; gap: .3rem; margin-top: .25rem; font-size: .73rem; color: " + MUTED + "; overflow-wrap: anywhere; }",
    ".login12-root .ld12-signout { min-width: 82px; min-height: 44px; flex-shrink: 0; font-size: .75rem; font-weight: 600; }",
    ".login12-root .ld12-dfoot { padding: .25rem 1.4rem 1.3rem; }",
    ".login12-root .ld12-cancel { width: 100%; min-height: 44px; font-weight: 600; color: #3f4556; }",

    "@media (prefers-reduced-motion: reduce) { .login12-root, .login12-root *, .login12-root *::before, .login12-root *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; transition-duration: .01ms !important; } }",
  ].join("\n");

  return (
    <ThemeScope vars={THEME_VARS} className="login12-root ld12-page">
      <style>{css}</style>

      {/* ── DECORATIVE BACKGROUND (static, non-interactive) ── */}
      <div className="ld12-bg" aria-hidden="true">
        <div className="ld12-grid" />
        <div className="ld12-glow" />
      </div>

      <div className="ld12-shell">
        {/* Logo */}
        <motion.div className="ld12-logo" {...enter(0.02, -8)}>
          <img src="/RBLOGOBLUE.svg" alt="ReonBook" />
        </motion.div>

        {/* CARD */}
        <div className="ld12-cardcol">
          <MotionCard
            className="ld12-card"
            {...(reduce ? {} : {
              initial: { y: 18, scale: 0.985 },
              animate: { y: 0, scale: 1 },
              transition: { duration: 0.6, delay: 0.05, ease: EASE },
            })}
          >
            <AnimatePresence mode="wait">
              {!isCompanySelectionVisible ? (

                /* ── LOGIN FORM ── */
                <motion.div
                  key="login"
                  exit={{ opacity: 0, x: reduce ? 0 : -14, transition: { duration: 0.15 } }}
                >
                  <CardHeader className="ld12-head">
                    <motion.div className="ld12-tile" aria-hidden="true" {...enter(0.1, 8)}>
                      <LockOutlinedIcon sx={{ fontSize: 22 }} />
                    </motion.div>
                    <motion.div className="ld12-headtext" {...enter(0.14, 8)}>
                      <CardTitle role="heading" aria-level={1} className="ld12-title">Welcome</CardTitle>
                      <CardDescription className="ld12-desc">Sign in to access your ReonBook dashboard</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent>
                    <form onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>

                      <motion.div className="ld12-group" {...slide(0.18)}>
                        <Label htmlFor="ld12-identifier" className="ld12-label" style={{ display: "block", marginBottom: "0.45rem" }}>
                          Username, Email or Mobile
                        </Label>
                        <div className="ld12-field">
                          <PersonOutlineIcon className="ld12-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                          <Input
                            id="ld12-identifier"
                            type="text"
                            autoComplete="username"
                            placeholder="Username, email or mobile number"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                          />
                          {capsLockOn && <WarningAmberIcon className="ld12-capsicon" sx={{ color: "#f59e0b", fontSize: 20 }} aria-hidden="true" />}
                        </div>
                        {capsHelper}
                      </motion.div>

                      <motion.div className="ld12-group" style={{ marginTop: "0.6rem" }} {...slide(0.24)}>
                        <div className="ld12-labelrow">
                          <Label htmlFor="ld12-password" className="ld12-label">Password</Label>
                          <Link to="/forgot-password" className="ld12-link">Forgot password?</Link>
                        </div>
                        <div className="ld12-field">
                          <LockOutlinedIcon className="ld12-fieldicon" sx={{ fontSize: 22 }} aria-hidden="true" />
                          <Input
                            id="ld12-password"
                            className="ld12-pw"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            placeholder="Enter your password"
                            value={userPassword}
                            onChange={(e) => setUserPassword(e.target.value)}
                            onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)}
                          />
                          {capsLockOn && <WarningAmberIcon className="ld12-capsicon ld12-capsicon-pw" sx={{ color: "#f59e0b", fontSize: 20 }} aria-hidden="true" />}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ld12-toggle"
                            onClick={handleTogglePasswordVisibility}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            aria-pressed={showPassword}
                          >
                            {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                          </Button>
                        </div>
                        {capsHelper}
                      </motion.div>

                      <motion.div style={{ marginTop: "0.9rem" }} {...enter(0.3, 8)}>
                        {errorAlert}

                        <MotionButton
                          type="submit"
                          size="lg"
                          disabled={isLoading}
                          aria-busy={isLoading}
                          className="ld12-primary"
                          whileHover={reduce || isLoading ? undefined : { y: -1 }}
                          whileTap={reduce || isLoading ? undefined : { scale: 0.98 }}
                          transition={{ type: "spring", stiffness: 400, damping: 24 }}
                        >
                          {isLoading
                            ? <><CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />Signing in...</>
                            : <>Sign In<EnterKey /></>}
                        </MotionButton>

                        {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                          <Button
                            type="button"
                            variant="outline"
                            className="ld12-bio"
                            onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
                            Login with Biometric
                          </Button>
                        )}
                      </motion.div>
                    </form>
                  </CardContent>

                  <Separator />
                  <CardFooter>
                    <motion.p style={{ margin: 0 }} {...enter(0.38, 6)}>
                      Don't have an account?{" "}
                      <Link to="/register" className="ld12-link" style={{ fontSize: "inherit", minHeight: 0 }}>
                        Sign up now
                      </Link>
                    </motion.p>
                    <div className="ld12-hints" aria-hidden="true">
                      <span className="ld12-hint"><Kbd>Tab</Kbd>Next field</span>
                      <span className="ld12-hint"><EnterKey />Sign in</span>
                    </div>
                  </CardFooter>
                </motion.div>

              ) : (

                /* ── COMPANY SELECTION ── */
                <motion.div
                  key="company"
                  initial={reduce ? undefined : { x: 14 }}
                  animate={{ x: 0 }}
                  exit={{ opacity: 0, transition: { duration: 0.15 } }}
                  transition={{ duration: 0.3, ease: EASE }}
                >
                  <CardHeader className="ld12-head">
                    <motion.div className="ld12-tile" aria-hidden="true" {...enter(0.05, 8)}>
                      <BusinessIcon sx={{ fontSize: 22 }} />
                    </motion.div>
                    <motion.div className="ld12-headtext" {...enter(0.08, 8)}>
                      <CardTitle role="heading" aria-level={1} className="ld12-title">Select Organization</CardTitle>
                      <CardDescription className="ld12-desc" id="ld12-company-desc">Choose which organization to access</CardDescription>
                    </motion.div>
                  </CardHeader>

                  <CardContent>
                    <div
                      className="ld12-companylist"
                      role="radiogroup"
                      aria-label="Choose your organization"
                      aria-describedby="ld12-company-desc"
                    >
                      {companies.map((c, idx) => {
                        const isSel = selectedCompany?.companyId === c.companyId;
                        const firstFocusable = !selectedCompany && idx === 0;
                        return (
                          <MotionCard
                            key={c.companyId}
                            id={"ld12-company-" + idx}
                            role="radio"
                            aria-checked={isSel}
                            tabIndex={isSel || firstFocusable ? 0 : -1}
                            className="ld12-company"
                            onClick={() => setSelectedCompany(companies.find((x) => x.companyId === c.companyId))}
                            onKeyDown={(e) => handleCompanyKeyDown(e, idx)}
                            {...enter(0.1 + Math.min(idx, 8) * 0.05, 8)}
                            whileHover={reduce ? undefined : { y: -1 }}
                            whileTap={reduce ? undefined : { scale: 0.99 }}
                          >
                            <AnimatePresence initial={false}>
                              {isSel && (
                                <motion.span
                                  key="ring"
                                  className="ld12-selring"
                                  aria-hidden="true"
                                  initial={{ opacity: 1 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: fadeDur }}
                                />
                              )}
                            </AnimatePresence>
                            <span className="ld12-avatar" aria-hidden="true">{(c.companyName || "?").trim().charAt(0).toUpperCase()}</span>
                            <span className="ld12-companyname">{c.companyName}</span>
                            <span className="ld12-radio" aria-hidden="true">
                              <AnimatePresence initial={false}>
                                {isSel && (
                                  <motion.span
                                    key="tick"
                                    className="ld12-radiofill"
                                    initial={{ scale: 0.4, opacity: 1 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.4, opacity: 0 }}
                                    transition={{ duration: fadeDur }}
                                  >
                                    <CheckIcon sx={{ fontSize: 14 }} />
                                  </motion.span>
                                )}
                              </AnimatePresence>
                            </span>
                          </MotionCard>
                        );
                      })}
                    </div>

                    <motion.div {...enter(0.2, 8)}>
                      {errorAlert}

                      <MotionButton
                        type="button"
                        size="lg"
                        className="ld12-primary"
                        onClick={() => FN_SelectCompany()}
                        whileHover={reduce ? undefined : { y: -1 }}
                        whileTap={reduce ? undefined : { scale: 0.98 }}
                        transition={{ type: "spring", stiffness: 400, damping: 24 }}
                      >
                        Continue to Dashboard<EnterKey />
                      </MotionButton>
                    </motion.div>
                  </CardContent>

                  <Separator />
                  <CardFooter>
                    <div className="ld12-hints" aria-hidden="true">
                      <span className="ld12-hint"><Kbd>↑</Kbd><Kbd>↓</Kbd>Navigate</span>
                      <span className="ld12-hint"><Kbd>Space</Kbd>Select</span>
                      <span className="ld12-hint"><EnterKey />Continue</span>
                    </div>
                  </CardFooter>
                </motion.div>
              )}
            </AnimatePresence>
          </MotionCard>
        </div>

        {/* HERO */}
        <section className="ld12-hero" aria-label="About ReonBook">
          <motion.div {...enter(0.1, 10)}>
            <Badge variant="outline" className="ld12-eyebrow">
              <span className="ld12-dot" aria-hidden="true" />
              Enterprise Accounting Platform
            </Badge>
          </motion.div>
          <motion.h2 className="ld12-headline" {...enter(0.18, 12)}>
            Accounting that stays <span className="ld12-accent">out of your way.</span>
          </motion.h2>
          <motion.p className="ld12-sub" {...enter(0.26, 12)}>
            Invoices, GST compliance, cash flow and multi-company ledgers — together in one clean workspace.
          </motion.p>
          <motion.ul className="ld12-chips" {...enter(0.34, 10)}>
            <li>GST ready</li>
            <li>Multi-company</li>
            <li>Secure sessions</li>
          </motion.ul>
        </section>

        {/* Footer */}
        <motion.p className="ld12-foot" {...enter(0.5, 0)}>
          <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
          {new Date().getFullYear()} Powered by{" "}
          Soltrack Technologies Pvt Ltd
        </motion.p>
      </div>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="login12-root"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="ld12-session-title"
        slots={{ transition: Grow }}
        transitionDuration={{ enter: 320, exit: 180 }}
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(15,18,34,0.42)" } },
          paper: { sx: { borderRadius: "16px", overflow: "hidden", m: 2, width: "calc(100% - 32px)", maxHeight: "calc(100% - 32px)" } },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={THEME_VARS} className="login12-root">
            <div className="ld12-dhead">
              <div className="ld12-tile" aria-hidden="true">
                <DevicesIcon sx={{ fontSize: 22 }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <h2 id="ld12-session-title" className="ld12-dtitle">Session Limit Reached</h2>
                <p className="ld12-dsub">
                  {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                </p>
              </div>
            </div>
            <div className="ld12-dnote">Sign out of one of the devices below to log in on this device.</div>

            <div className="ld12-dlist">
              <p className="ld12-dlabel">Active Sessions</p>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <motion.div
                  key={session.id}
                  className="ld12-session"
                  {...(reduce ? {} : {
                    initial: { opacity: 1, y: 12 },
                    animate: { opacity: 1, y: 0 },
                    transition: { duration: 0.4, delay: 0.1 + idx * 0.06, ease: EASE },
                  })}
                >
                  <div className="ld12-sicon">{deviceIcon(session.device_type)}</div>

                  <div className="ld12-sinfo">
                    <p className="ld12-sname">{session.device_name || "Unknown Device"}</p>
                    <div className="ld12-smeta">
                      <AccessTimeIcon sx={{ fontSize: 12, color: MUTED }} />
                      <span>{timeAgo(session.last_activity)}</span>
                      {session.ip_address && <span>· {session.ip_address}</span>}
                    </div>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="ld12-signout"
                    disabled={kickingSessionId === session.id}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </Button>
                </motion.div>
              ))}
            </div>

            <div className="ld12-dfoot">
              <Button
                type="button"
                variant="outline"
                className="ld12-cancel"
                onClick={() => setSessionPickerData(null)}
              >
                Cancel<Kbd>Esc</Kbd>
              </Button>
            </div>
          </ThemeScope>
        </DialogContent>
      </Dialog>

    </ThemeScope>
  );
}

export default LoginDesign12;
